import React, { useEffect, useRef, useState } from 'react';
import { Button, Group, Progress, Select, Stack, Switch, Text } from '@mantine/core';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from 'react-query';
import { fetchDownloadFiles, uploadDownloadFile } from '../../requests/downloadFiles';
import { getWebLinkSource } from '../../utils/webLinkRegistry';
import { isWebTemplateCatalog } from '../../utils/variableCatalogRegistry';
import { MAX_MEDIA_SIZE, MAX_MEDIA_SIZE_LABEL } from '../../../shared/media/mediaLimits';

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false, cacheTime: 0 } } });

export const DOCUMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.txt,.csv,.zip';

function DocumentDownloadInput({ value = {}, onChange, withFormSettings = false }) {
  const input = useRef(null);
  const client = useQueryClient();
  const source = getWebLinkSource();
  const requestRef = useRef(0);
  const currentRef = useRef({ value, onChange });
  currentRef.current = { value, onChange };
  useEffect(() => () => { requestRef.current += 1; }, [source?.type, source?.id]);
  const key = ['download-files', source?.type, source?.id];
  const { data = [], isLoading, isError } = useQuery(key, fetchDownloadFiles, { enabled: !!source?.id });
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const patch = (next) => currentRef.current.onChange({ ...currentRef.current.value, ...next, kind: 'download' });
  const options = data.map((file) => ({ value: String(file.id), label: file.filename }));
  if (value.documentId && !options.some((option) => option.value === String(value.documentId))) {
    options.unshift({ value: String(value.documentId), label: value.filename || 'Document sélectionné' });
  }
  const upload = async (file) => {
    if (!file) return;
    if (file.size >= MAX_MEDIA_SIZE) { setError(`Document trop lourd (${MAX_MEDIA_SIZE_LABEL} maximum).`); return; }
    if (!DOCUMENT_ACCEPT.split(',').some((extension) => file.name.toLowerCase().endsWith(extension))) {
      setError('Format non accepté. Choisissez un PDF, document bureautique, texte ou ZIP.');
      return;
    }
    const request = ++requestRef.current;
    setError(''); setProgress(0); setUploading(true);
    try {
      const uploaded = await uploadDownloadFile(file, setProgress);
      if (request !== requestRef.current) return;
      client.setQueryData(key, (files = []) => [...files.filter((file) => String(file.id) !== String(uploaded.id)), uploaded]);
      patch({ url: uploaded.url, filename: uploaded.filename, documentId: uploaded.id });
    } catch {
      if (request !== requestRef.current) return;
      setError('L’envoi du document a échoué. Réessayez.');
    } finally {
      if (request === requestRef.current) { setUploading(false); if (input.current) input.current.value = ''; }
    }
  };
  return (
    <Stack gap="xs">
      <Select label="Document à télécharger" placeholder={isLoading ? 'Chargement…' : 'Choisir un document'}
        data={options} value={value.documentId ? String(value.documentId) : null} searchable clearable
        wrapperProps={{ 'data-inspector-layout': 'stacked' }}
        title={value.filename || undefined}
        renderOption={({ option }) => <Text size="sm" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{option.label}</Text>}
        disabled={uploading} comboboxProps={{ withinPortal: false }}
        onChange={(id) => {
          const file = data.find((candidate) => String(candidate.id) === id);
          if (file) patch({ url: file.url, filename: file.filename, documentId: file.id });
          else if (!id) patch({ url: '', filename: '', documentId: null });
        }} />
      {isError && <Text size="xs" role="alert" c="red">La liste des documents est indisponible.</Text>}
      {value.url && <Text size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>{value.filename || 'Document sélectionné'}</Text>}
      <Group>
        <Button size="xs" variant="light" disabled={!source?.id || uploading} loading={uploading} onClick={() => input.current?.click()}>Importer un document</Button>
        {value.url && <Button size="xs" variant="subtle" disabled={uploading} onClick={() => patch({ url: '', filename: '', documentId: null })}>Retirer le document</Button>}
      </Group>
      <input ref={input} type="file" accept={DOCUMENT_ACCEPT} hidden aria-label="Importer un document" onChange={(event) => upload(event.target.files?.[0])} />
      {uploading && <Progress value={progress} aria-label="Envoi du document" />}
      {error && <Text size="xs" role="alert" c="red">{error}</Text>}
      {withFormSettings && isWebTemplateCatalog() && <Switch size="sm" label="Choisir le document dans le formulaire" checked={!!value.form?.showInForm} onChange={(event) => patch({ form: { ...value.form, showInForm: event.currentTarget.checked } })} />}
    </Stack>
  );
}


export default function DocumentDownloadField(props) {
  return <QueryClientProvider client={queryClient}><DocumentDownloadInput {...props} /></QueryClientProvider>;
}
